import {
  Area,
  AreaChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { useTranslation } from 'react-i18next'
import { useSettingsStore } from '@/state/useSettingsStore'
import { formatCzkShort, formatDate } from '@/lib/format'

interface Props {
  data: Array<{ t: string; v: number }>
  targetCzk?: number
  height?: number
}

export function RevenueArea({ data, targetCzk, height = 220 }: Props) {
  const locale = useSettingsStore((s) => s.locale)
  const { t } = useTranslation()
  const points = data.map((d) => ({ t: d.t, v: d.v }))
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={points} margin={{ top: 12, right: 8, left: 8, bottom: 0 }}>
        <defs>
          <linearGradient id="revenue-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--brand-primary)" stopOpacity={0.36} />
            <stop offset="100%" stopColor="var(--brand-primary)" stopOpacity={0} />
          </linearGradient>
        </defs>
        <XAxis
          dataKey="t"
          axisLine={false}
          tickLine={false}
          stroke="var(--ink-subtle)"
          fontSize={11}
          tickFormatter={(iso) => {
            const d = new Date(iso)
            return d.toLocaleDateString(locale === 'cs' ? 'cs-CZ' : 'en-GB', {
              day: 'numeric',
              month: 'short',
            })
          }}
          minTickGap={28}
        />
        <YAxis hide domain={['auto', 'auto']} />
        <Tooltip
          cursor={{ stroke: 'var(--border-strong)', strokeWidth: 1 }}
          contentStyle={{
            background: 'var(--surface-1)',
            border: '1px solid var(--border)',
            borderRadius: 12,
            fontSize: 12,
            color: 'var(--ink)',
          }}
          labelFormatter={(iso) => formatDate(new Date(iso as string), locale)}
          formatter={(v) => [formatCzkShort(Number(v), locale), 'MRR']}
        />
        {targetCzk && (
          <ReferenceLine
            y={targetCzk}
            stroke="var(--brand-accent)"
            strokeDasharray="3 3"
            label={{
              value: `${t('common.vs_target')} ${formatCzkShort(targetCzk, locale)}`,
              position: 'insideTopRight',
              fill: 'var(--brand-accent)',
              fontSize: 11,
            }}
          />
        )}
        <Area
          type="monotone"
          dataKey="v"
          stroke="var(--brand-primary)"
          strokeWidth={2.2}
          fill="url(#revenue-fill)"
        />
      </AreaChart>
    </ResponsiveContainer>
  )
}
